import { InputField } from 'lib/iterable-hook-form';
import { TriggerHoldUntilReplyRuleType } from 'modules/workflows/constants/nodes/holdUntilReply';
import { useCallback, useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { Accordion, AccordionContent, AccordionHeader } from 'ui/accordion';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { useDragAndDropCallbacks, type DragAndDropCallback } from './hooks/useDragAndDropCallbacks';
import { useQuickReplies } from './hooks/useQuickReplies';
import { ReplyHeader } from './ReplyHeader';

import { AddNew16 } from 'ui/icons/aurora/16px/AddNew16';
import { Drag16 } from 'ui/icons/aurora/16px/Drag16';
import { Exit24 } from 'ui/icons/aurora/24px/Exit24';
import { Reply24 } from 'ui/icons/aurora/24px/Reply24';
import { spacing } from 'ui/theme';
import { CAMPAIGN_ID, REPLIES, TRIGGER_RULE_TYPE } from './HoldUntilReply.constants';
import {
  AddNewReplySectionButtonText,
  AddVariationButtonText,
  DropReplyHereContainer,
  DropReplyHereText,
  FormFieldLabel,
  RepliesContainer,
  VariantContainer,
  VariantRow,
  VariantsList,
} from './HoldUntilReply.styles';
import { Reply } from './HoldUntilReply.types';
import { UngroupedQuickReplies } from './UngroupedQuickReplies';
import { hasAtLeastOneReplyVariant } from './utils';

const DroppableContainer = styled.div`
  width: 100%;
`;

const StyledDraggable = styled(Draggable)`
  width: 100%;
`;

const StyledAccordion = styled(Accordion)`
    width: 100%;
`;
const StyledAccordionContent = styled(AccordionContent)`
  padding: 1px;
  &[data-state='open'] {
      height: auto;
  }
`;

const StyledInput = styled(InputField)`
    width: 100%;
`;
const StyledAddReplyButton = styled(Button)`
    justify-content: flex-start;
    width: 100%;
    padding: ${spacing.plusThreeQuarter};
`;

const StyledDragIcon = styled(Drag16)`
    flex-shrink: 0;
`;

// TEST IDS
export const REPLY_LIST_TEST_ID = 'reply-list';
export const ADD_NEW_REPLY_SECTION_BUTTON_TEST_ID = 'add-new-reply-section-button';
export const REPLIES_TEST_ID = 'replies';

const VARIATION = {
  variant: '',
};

const MAX_INPUT_SIZE = 255;

export const DropReplyHere = () => {
  return (
    <DropReplyHereContainer>
      <DropReplyHereText>Drop reply here to group or</DropReplyHereText>
    </DropReplyHereContainer>
  );
};

export const ReplyList = ({
  replySectionIndex,
  removeReply,
  isReadOnly = false,
  registerDragAndDropCallbacks,
}: {
  replySectionIndex: number;
  removeReply?: () => void;
  registerDragAndDropCallbacks: (
    replyIndex: string,
    { addReplyVariant, removeReplyVariant, moveReplyVariant }: DragAndDropCallback,
  ) => void;
  isReadOnly?: boolean;
}) => {
  const [showDropHere, setShowDropHere] = useState(false);
  const [showDeleteButton, setShowDeleteButton] = useState(false);

  const [showPills, setShowPills] = useState(isReadOnly);

  const { control } = useFormContext();
  const [campaignId, triggerRuleType, replies] = useWatch({
    control,
    name: [CAMPAIGN_ID, TRIGGER_RULE_TYPE, REPLIES],
  });

  const latestReplyVariants = replies?.[replySectionIndex]?.variants || [];

  const { quickReplies } = useQuickReplies();

  const {
    fields: replyVariants,
    append,
    remove,
    insert,
    move,
  } = useFieldArray({ control, name: `replies[${replySectionIndex}].variants` });

  const canShowDropHere = useCallback(() => {
    return (
      triggerRuleType === TriggerHoldUntilReplyRuleType.WHATSAPP &&
      campaignId &&
      quickReplies.length > 0 &&
      (replyVariants.at(0) as { id: string; variant: string })?.variant === ''
    );
  }, [triggerRuleType, campaignId, quickReplies, replyVariants]);

  useEffect(() => {
    registerDragAndDropCallbacks(`reply-${replySectionIndex}`, {
      addReplyVariant: (value: string, index: number) => {
        if (latestReplyVariants?.[0]?.variant === '') {
          remove(0);
        }
        if (showPills) {
          append({ variant: value });
        } else {
          insert(index, { variant: value });
        }
        if (showDropHere) {
          setShowDropHere(false);
        }
      },
      removeReplyVariant: (index: number) => {
        remove(index);
      },
      moveReplyVariant: (from: number, to: number) => {
        move(from, to);
      },
    });
  }, [
    replySectionIndex,
    registerDragAndDropCallbacks,
    insert,
    append,
    remove,
    move,
    showDropHere,
    latestReplyVariants,
    showPills,
  ]);

  useEffect(() => {
    if (latestReplyVariants?.length === 0) {
      append({ variant: '' });
      if (
        triggerRuleType === TriggerHoldUntilReplyRuleType.WHATSAPP &&
        campaignId &&
        quickReplies.length > 0
      ) {
        setShowDropHere(true);
      }
    }
  }, [append, latestReplyVariants]);

  useEffect(() => {
    if (canShowDropHere()) {
      setShowDropHere(true);
    }
  }, [triggerRuleType, campaignId]);

  const transformInput = (value: string) => value.toLocaleLowerCase();

  return (
    <Droppable droppableId={`reply-${replySectionIndex}`}>
      {(droppableProvided) => (
        <DroppableContainer ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
          <StyledAccordion
            defaultValue={isReadOnly ? '' : 'item-1'}
            disabled={isReadOnly}
            onValueChange={(value) => setShowPills(!value)}
            onMouseEnter={() => setShowDeleteButton(true)}
            onMouseLeave={() => setShowDeleteButton(false)}
          >
            <AccordionHeader>
              <ReplyHeader
                isReadOnly={isReadOnly}
                replySectionIndex={replySectionIndex}
                showPills={showPills}
                onRemove={remove}
                showDeleteButton={!!removeReply && showDeleteButton && !isReadOnly}
                onDelete={() => removeReply?.()}
              />
            </AccordionHeader>
            <StyledAccordionContent>
              <VariantContainer>
                <VariantsList>
                  {showDropHere ? (
                    <DropReplyHere />
                  ) : (
                    replyVariants.map((item: { id: string }, index: number) => (
                      <StyledDraggable
                        key={index}
                        draggableId={`reply-${replySectionIndex}-variant-${index.toString()}`}
                        index={index}
                      >
                        {(draggableProvided) => {
                          return (
                            <VariantRow
                              key={`${item.id}`}
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.draggableProps}
                              {...draggableProvided.dragHandleProps}
                              data-test={`draggable-${index}`}
                            >
                              <StyledDragIcon />
                              <StyledInput
                                data-test={`variant-input-${replySectionIndex}-${index}`}
                                name={`replies[${replySectionIndex}].variants[${index}].variant`}
                                maxLength={MAX_INPUT_SIZE}
                                placeholder="Enter a reply..."
                                Prefix={
                                  quickReplies.includes(latestReplyVariants[index]?.variant) ? (
                                    <Reply24 />
                                  ) : null
                                }
                                transformInput={transformInput}
                              />
                              {replyVariants.length > 1 && (
                                <IconButton
                                  data-test={`remove-reply-${replySectionIndex}-${index}`}
                                  variant={BUTTON_VARIANTS.GHOST}
                                  size={BUTTON_SIZES.LARGE}
                                  onClick={() => {
                                    if (canShowDropHere()) {
                                      setShowDropHere(true);
                                    }
                                    remove(index);
                                  }}
                                >
                                  <Exit24 />
                                </IconButton>
                              )}
                            </VariantRow>
                          );
                        }}
                      </StyledDraggable>
                    ))
                  )}
                </VariantsList>
                {replyVariants?.length < 20 && (
                  <Button
                    data-test={`add-variant-${replySectionIndex}`}
                    variant={BUTTON_VARIANTS.GHOST}
                    onClick={() => {
                      if (showDropHere) {
                        setShowDropHere(false);
                      } else {
                        append(VARIATION);
                      }
                    }}
                  >
                    <AddNew16 /> <AddVariationButtonText>Add variation</AddVariationButtonText>
                  </Button>
                )}
              </VariantContainer>
            </StyledAccordionContent>
          </StyledAccordion>
        </DroppableContainer>
      )}
    </Droppable>
  );
};

export const AddNewReplySectionButton = ({ onAdd }: { onAdd: (reply: Reply) => void }) => (
  <StyledAddReplyButton
    data-test={ADD_NEW_REPLY_SECTION_BUTTON_TEST_ID}
    size={BUTTON_SIZES.LARGE}
    variant={BUTTON_VARIANTS.SECONDARY}
    onClick={() => onAdd({ variants: [VARIATION] })}
  >
    <AddNew16 /> <AddNewReplySectionButtonText>Add another reply</AddNewReplySectionButtonText>
  </StyledAddReplyButton>
);

export const Replies = ({
  onAddReply,
  onRemoveReply,
  isReadOnly = false,
}: { onAddReply: () => void; onRemoveReply: (idx: number) => void; isReadOnly?: boolean }) => {
  const { control } = useFormContext();
  const { fields: repliesFields, append, remove } = useFieldArray({ name: REPLIES, control });

  const [campaignId, triggerRuleType] = useWatch({
    control,
    name: [CAMPAIGN_ID, TRIGGER_RULE_TYPE, REPLIES],
  });
  const listOfReplies = useWatch({ name: REPLIES, control });
  const hasAtleastOneReplyVariant = hasAtLeastOneReplyVariant(listOfReplies);
  const { filteredQuickReplies, quickReplies } = useQuickReplies();

  const hasNotReachedMaxReplies = repliesFields?.length < 10;
  const hasMoreThanOneReply = repliesFields?.length > 1;

  const handleRemoveReply = (index: number) => {
    onRemoveReply(index);
    remove(index);
  };

  const handleAddReply = (reply: Reply) => {
    append(reply);
    onAddReply();
  };

  const { onDragEnd, registerDragAndDropCallbacks } = useDragAndDropCallbacks();

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <RepliesContainer data-test={REPLIES_TEST_ID}>
        <FormFieldLabel>Replies</FormFieldLabel>
        {repliesFields.map((reply: { id: string }, index: number) => (
          <ReplyList
            isReadOnly={isReadOnly}
            key={`${reply.id}`}
            replySectionIndex={index}
            removeReply={hasMoreThanOneReply ? () => handleRemoveReply(index) : undefined}
            registerDragAndDropCallbacks={registerDragAndDropCallbacks}
          />
        ))}
        {hasAtleastOneReplyVariant && hasNotReachedMaxReplies && !isReadOnly && (
          <AddNewReplySectionButton onAdd={handleAddReply} />
        )}
      </RepliesContainer>
      {triggerRuleType === TriggerHoldUntilReplyRuleType.WHATSAPP && campaignId && !isReadOnly && (
        <UngroupedQuickReplies
          filteredQuickReplies={filteredQuickReplies}
          quickReplies={quickReplies}
        />
      )}
    </DragDropContext>
  );
};
